
import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, X } from "lucide-react";

interface ProfileAvatarProps {
  userId: string;
  avatarUrl: string | null;
  fullName?: string | null;
  email?: string;
  readOnly?: boolean;
  onAvatarUpdated: (url: string | null) => void;
}

export function ProfileAvatar({ 
  userId, 
  avatarUrl, 
  fullName, 
  email,
  readOnly = false,
  onAvatarUpdated 
}: ProfileAvatarProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !userId) {
      return;
    }

    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const filePath = `${userId}/${crypto.randomUUID()}.${fileExt}`;

    // Create a preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setImagePreview(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);

    try {
      setUploading(true);

      // Upload the file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: publicURLData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update the user's profile with the new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicURLData.publicUrl })
        .eq('id', userId);

      if (updateError) throw updateError;

      onAvatarUpdated(publicURLData.publicUrl);
      setImagePreview(null);
      
      toast({
        title: "Avatar updated successfully",
      });
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast({
        title: "Failed to upload avatar",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!userId || !avatarUrl) return;

    try {
      setUploading(true);

      // Extract the file path from the URL
      const pathMatch = avatarUrl.match(/\/avatars\/([^?]+)/);
      if (!pathMatch) throw new Error("Invalid avatar URL");
      
      const filePath = pathMatch[1];

      // Delete the file from storage
      const { error: deleteError } = await supabase.storage
        .from('avatars')
        .remove([filePath]);

      if (deleteError) throw deleteError;

      // Update profile to remove the avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', userId);

      if (updateError) throw updateError;

      onAvatarUpdated(null);
      toast({
        title: "Avatar removed successfully",
      });
    } catch (error) {
      console.error("Error removing avatar:", error);
      toast({
        title: "Failed to remove avatar",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const getInitials = (name?: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <Avatar className="h-24 w-24 border-2 border-slate-200">
        {imagePreview ? (
          <AvatarImage src={imagePreview} alt="Preview" />
        ) : (
          <>
            <AvatarImage src={avatarUrl || ""} alt={fullName || email} />
            <AvatarFallback className="bg-slate-100 text-slate-600 text-xl">
              {getInitials(fullName)}
            </AvatarFallback>
          </>
        )}
      </Avatar>
      
      {!readOnly && (
        <div className="flex gap-2">
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            onChange={handleAvatarUpload}
            disabled={uploading}
            className="hidden"
          />
          <label htmlFor="avatar-upload">
            <Button 
              size="sm" 
              variant="outline" 
              className="cursor-pointer"
              asChild
              disabled={uploading}
            >
              <div>
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading</>
                ) : (
                  <><Upload className="h-4 w-4 mr-1" /> Upload</>
                )}
              </div>
            </Button>
          </label>
          
          {avatarUrl && (
            <Button 
              size="sm" 
              variant="destructive" 
              onClick={handleRemoveAvatar}
              disabled={uploading}
            >
              <X className="h-4 w-4 mr-1" />
              Remove
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
